import { t } from "@hakili/i18n";
import { Card, CHIFFRE, METADONNEE, TEXTE_SECONDAIRE, TEXTE_TERTIAIRE } from "@hakili/ui";
import { useEffect, useState } from "react";

import type { DoorResult, Inventory, InventoryVehicle } from "../session/useSession.js";

/**
 * `round F`, `F5`: **« Votre note de fiabilité »**, as
 * `hakili-dealer.jsx:3679-3760` draws it.
 *
 * ## The drawing's own division of this screen is what makes it buildable
 *
 * Its header comment says it exactly: *« Ce qui peut se dériver du parc se
 * dérive … Ce qui appartient à l'historique tenu par Hakili … est lu tel quel
 * sur votre compte »*. Two halves, two sources , and only one of the two has a
 * door.
 *
 * **Derived from the park**, and rendered: how many of this dealership's cars
 * carry an expertise report, and how many were stopped by a non-conforming one.
 * `me/vehicles` answers `expertiseReportId` and `stage`, so both are facts.
 *
 * **Held by Hakili**, and absent: the grade itself, the partner-since date, the
 * financed files and their volume, the average delay to make a car available,
 * the disputes. Every one of those lives in `partnerships`, and 05 2.2.1 says
 * in terms that `fiabiliteVendeur` *"is not a column: it is read from
 * `partnerships` at listing render time"* , a read with no route.
 *
 * The average price gap against expertise is the interesting case: it needs
 * each report's VALUATION, and `me/vehicles` carries the report's id and not
 * its contents. It is a derivation waiting on a door, not a figure waiting on a
 * decision, and it is named as such.
 *
 * ## Why no grade is shown, not even a placeholder letter
 *
 * A letter here is a claim a bank reads. The console shows Hakili's own view of
 * this same dealership, and a dealer space that printed « A » from nothing
 * would contradict it the first time a partner asked. `R81`, and `A48` beside
 * it: the slot is empty and says which module fills it.
 */

/** No right names this screen in 06 §6.1.7 , see `shell/DealerShell.tsx`'s entry. */
export interface ReliabilityProps {
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
}

export function Reliability({ listVehicles }: ReliabilityProps): React.JSX.Element {
  const [park, setPark] = useState<readonly InventoryVehicle[] | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void listVehicles().then((answer) => {
      if (!alive) return;
      if (answer.ok) setPark(answer.value.vehicles);
      else setRefus(answer.message);
    });
    return () => {
      alive = false;
    };
  }, [listVehicles]);

  const cars = park ?? [];
  const reported = cars.filter(
    (car) => car.expertiseReportId !== null || (car.reportVerdict ?? null) !== null,
  );
  const blocked = cars.filter((car) => car.stage === "nonConforme");

  return (
    <div className="flex flex-col gap-6" data-testid="fiabilite">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="fiabilite-titre">
          {t("dealer.fiabilite.titre")}
        </h1>
        <p className={`mt-1.5 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.fiabilite.resume")}</p>
      </div>

      {refus === null ? null : (
        <p role="alert" className="text-sm text-rose-700" data-testid="fiabilite-refus">
          {refus}
        </p>
      )}

      {/* The grade, held by Hakili and not by this screen. */}
      <Card>
        <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.fiabilite.note")}</p>
        <p className="mt-2 text-sm text-gray-500" data-testid="fiabilite-note-absente">
          {t("dealer.fiabilite.noteAbsente")}
        </p>
      </Card>

      {/* The half that IS derivable from the park, and only that half. */}
      <div className="grid grid-cols-2 gap-4" data-testid="fiabilite-chiffres">
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.fiabilite.controles")}</p>
          <p className={`mt-1 text-2xl font-semibold ${CHIFFRE}`} data-testid="fiabilite-controles">
            {String(reported.length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.fiabilite.controlesSuite")}</p>
        </Card>
        <Card>
          <p className={`${METADONNEE} ${TEXTE_SECONDAIRE}`}>{t("dealer.fiabilite.nonConformes")}</p>
          <p className={`mt-1 text-2xl font-semibold ${CHIFFRE}`} data-testid="fiabilite-non-conformes">
            {String(blocked.length)}
          </p>
          <p className={`mt-1 ${METADONNEE} ${TEXTE_TERTIAIRE}`}>{t("dealer.fiabilite.nonConformesSuite")}</p>
        </Card>
      </div>

      {/* `R81`: everything the drawing shows that Hakili holds, named. */}
      <p className={`${METADONNEE} ${TEXTE_TERTIAIRE}`} data-testid="fiabilite-en-attente">
        {t("dealer.fiabilite.enAttente")}
      </p>
    </div>
  );
}
